import { PaginationResult } from '@shared/pagination';
import { maxLength, required, validateIf } from '@shared/validators';
import { ReactNode, useEffect, useId } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { Busy } from '@/components/busy';
import { LazyLoadSelectList } from '@/components/form-controls';
import { Button } from '@/components-new/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/components-new/dialog';
import { ErrorMessage, Field, Label } from '@/components-new/fieldset';
import { Input } from '@/components-new/input';
import { Radio, RadioField, RadioGroup } from '@/components-new/radio';
import { DrugLookupSelect } from '@/features/market-baskets/components/drug-lookup-select';
import {
  DRUG_LOOKUP_ASSOCIATION,
  DrugLookupAssociationType
} from '@/features/market-baskets/types/drug-lookup-associations';
import { useLookupsService } from '@/hooks/use-lookups-service';
import { Lookup } from '@/types/lookup';

type DrugLookupDialogProps = {
  header: ReactNode;
  hint?: string;
  open: boolean;
  onSubmit: (data: FormInputs) => void;
  onClose: () => void;
  mode: 'existing-only' | 'new-and-existing';
  saving: boolean;
  submitButtonLabel: ReactNode;
};

export const DrugLookupDialog = ({ header, hint, open, onSubmit, onClose, mode, saving, submitButtonLabel }: DrugLookupDialogProps) => {
  const formId = useId();

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    reset,
  } = useForm<FormInputs>({
    mode: 'all',
    defaultValues: {
      associationType: DRUG_LOOKUP_ASSOCIATION.EXISTING_PRODUCT,
      productName: ''
    }
  });

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const associationType = useWatch({ control, name: 'associationType' });
  const associatingWithExistingProduct = associationType === DRUG_LOOKUP_ASSOCIATION.EXISTING_PRODUCT;
  const creatingNewProduct = associationType === DRUG_LOOKUP_ASSOCIATION.NEW_PRODUCT;
  const submitDisabled = !isValid || saving;

  const { getClientLookups } = useLookupsService();

  return (
    <Dialog open={open} onClose={saving ? () => {} : onClose}>
      <DialogTitle>{header}</DialogTitle>
      {hint && <p className="mt-1 text-sm text-zinc-500">{hint}</p>}
      <DialogBody>
        <form id={formId} className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-3">
            {mode === 'new-and-existing' ? (
              <Controller
                control={control}
                name="associationType"
                render={({ field: { onChange, value } }) => (
                  <RadioGroup value={value} onChange={onChange}>
                    <RadioField>
                      <Radio value={DRUG_LOOKUP_ASSOCIATION.EXISTING_PRODUCT} />
                      <Label>Select existing product</Label>
                    </RadioField>
                    <RadioField>
                      <Radio value={DRUG_LOOKUP_ASSOCIATION.NEW_PRODUCT} />
                      <Label>Create new product</Label>
                    </RadioField>
                  </RadioGroup>
                )}
              />
            ) : null}

            <Controller
              control={control}
              rules={{
                validate: {
                  required: validateIf(associatingWithExistingProduct, required('Product is required'))
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <DrugLookupSelect
                  required={associatingWithExistingProduct}
                  disabled={!associatingWithExistingProduct}
                  error={associatingWithExistingProduct && error?.message}
                  {...field}
                />
              )}
              name="product"
            />
          </div>

          {mode === 'new-and-existing' && creatingNewProduct ? (
            <div className="flex flex-row gap-3">
              <Controller
                control={control}
                defaultValue=""
                rules={{
                  validate: {
                    required: validateIf(creatingNewProduct, required('Product name is required')),
                    maxLength: validateIf(creatingNewProduct, maxLength(100, 'Product name must be less than 100 characters'))
                  }
                }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <Field disabled={!creatingNewProduct}>
                    <Label>Product Name</Label>
                    <Input
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      disabled={!creatingNewProduct}
                      id="productName"
                      name="productName"
                      ref={ref}
                      invalid={creatingNewProduct && !!errors.productName}
                    />
                    {creatingNewProduct && errors.productName?.message && (
                      <ErrorMessage>{errors.productName.message}</ErrorMessage>
                    )}
                  </Field>
                )}
                name="productName"
              />
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <LazyLoadSelectList
                    error={error?.message}
                    label="Client"
                    disabled={!creatingNewProduct}
                    placeholder="Select a client"
                    {...field}
                    lazyLoadRequest={async (searchTerm, page, rpp) => {
                      const result = await getClientLookups({ query: searchTerm, page, rpp });
                      return result as PaginationResult<Lookup>;
                    }}
                  />
                )}
                name="client"
              />
            </div>
          ) : null}
        </form>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          type="submit"
          form={formId}
          disabled={submitDisabled}
        >
          <Busy busy={saving} content={submitButtonLabel}/>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type FormInputs = {
  associationType: DrugLookupAssociationType;
  productName: string;
  client: Lookup;
  product: Lookup;
};
